import React, { ReactNode } from 'react';

import styled from 'styled-components';

import { useMobileContext } from '../MobileContext';

export interface ActionBarLayoutProps {
  /** Action button content */
  children: ReactNode;
  /** Layout direction */
  direction?: 'row' | 'column';
  /** Alignment of actions */
  align?: 'start' | 'center' | 'end' | 'space-between' | 'space-around';
  /** Custom className */
  className?: string;
  /** Wrap buttons on small screens */
  allowWrap?: boolean;
  /** Mobile behavior */
  mobileConfig?: {
    /** Stack buttons vertically on mobile */
    stackOnMobile?: boolean;
    /** Hide action bar on mobile */
    hideOnMobile?: boolean;
    /** Compact spacing on mobile */
    compactSpacing?: boolean;
  };
  /** Custom gap between actions */
  gap?: string;
  /** Show as floating action bar */
  floating?: boolean;
}

/**
 * Standardized action bar layout component for button groups.
 * Replaces custom styled button containers throughout the application.
 *
 * Features:
 * - Flexible button arrangements
 * - Responsive mobile behavior
 * - Theme-based spacing
 * - Floating action bar support
 * - Consistent button styling
 *
 * @example
 * // Header action buttons
 * <ActionBarLayout align="end" mobileConfig={{ stackOnMobile: true }}>
 *   <Button variant="primary">Save</Button>
 *   <Button variant="secondary">Cancel</Button>
 * </ActionBarLayout>
 *
 * @example
 * // Floating mobile actions
 * <ActionBarLayout
 *   floating
 *   direction="column"
 *   mobileConfig={{ compactSpacing: true }}
 * >
 *   <IconButton icon="upload" />
 *   <IconButton icon="share" />
 * </ActionBarLayout>
 */
export const ActionBarLayout: React.FC<ActionBarLayoutProps> = ({
  children,
  direction = 'row',
  align = 'start',
  className,
  allowWrap = false,
  mobileConfig = {},
  gap,
  floating = false,
}) => {
  const { isMobile } = useMobileContext();

  const { stackOnMobile = false, hideOnMobile = false, compactSpacing = false } = mobileConfig;

  // Hide on mobile if configured
  if (isMobile && hideOnMobile) {
    return null;
  }

  // Determine effective direction
  const effectiveDirection = isMobile && stackOnMobile ? 'column' : direction;

  return (
    <StyledActionBarLayout
      className={className}
      $direction={effectiveDirection}
      $align={align}
      $isMobile={isMobile}
      $allowWrap={allowWrap}
      $compactSpacing={compactSpacing}
      $floating={floating}
      $gap={gap}
    >
      {children}
    </StyledActionBarLayout>
  );
};

ActionBarLayout.displayName = 'ActionBarLayout';

// Styled Components
interface StyledProps {
  $direction: string;
  $align: string;
  $isMobile: boolean;
  $allowWrap: boolean;
  $compactSpacing: boolean;
  $floating: boolean;
  $gap?: string;
}

const StyledActionBarLayout = styled.div<StyledProps>`
  /* Flexbox layout for action buttons */
  display: flex;
  flex-direction: ${({ $direction }) => $direction};

  /* Alignment based on direction */
  ${({ $direction, $align }) => {
    if ($direction === 'column') {
      switch ($align) {
        case 'start':
          return 'align-items: flex-start;';
        case 'center':
          return 'align-items: center;';
        case 'end':
          return 'align-items: flex-end;';
        case 'space-between':
          return 'justify-content: space-between;';
        case 'space-around':
          return 'justify-content: space-around;';
        default:
          return 'align-items: flex-start;';
      }
    } else {
      switch ($align) {
        case 'start':
          return 'justify-content: flex-start;';
        case 'center':
          return 'justify-content: center;';
        case 'end':
          return 'justify-content: flex-end;';
        case 'space-between':
          return 'justify-content: space-between;';
        case 'space-around':
          return 'justify-content: space-around;';
        default:
          return 'justify-content: flex-start;';
      }
    }
  }}

  /* Gap between actions */
  gap: ${({ theme, $gap, $compactSpacing }) => {
    if ($gap) return $gap;
    if ($compactSpacing) return theme.space.spacing10;
    return theme.space.spacing10;
  }};

  /* Wrapping behavior */
  flex-wrap: ${({ $allowWrap }) => ($allowWrap ? 'wrap' : 'nowrap')};

  /* Floating action bar styling */
  ${({ $floating, theme }) =>
    $floating &&
    `
    position: fixed;
    bottom: ${theme.space.spacing20};
    right: ${theme.space.spacing20};
    z-index: 1000;
    background-color: ${theme.colors.palette.white};
    border-radius: ${theme.radii.scale[2]};
    box-shadow: ${theme.shadows.primary};
    padding: ${theme.space.spacing10};
    border: 1px solid ${theme.colors.borderBase};
  `}

  /* Mobile-specific adjustments */
  @media (max-width: 768px) {
    gap: ${({ theme, $gap, $compactSpacing }) => {
      if ($gap) return $gap;
      if ($compactSpacing) return theme.space.spacing10;
      return theme.space.spacing10;
    }};

    ${({ $floating, theme }) =>
      $floating &&
      `
      bottom: ${theme.space.spacing10};
      right: ${theme.space.spacing10};
      left: ${theme.space.spacing10};

      /* Full width on mobile for floating bars */
      flex-direction: row;
      justify-content: space-evenly;
    `}

    /* Ensure buttons are touch-friendly on mobile */
    > * {
      min-height: 44px;
      min-width: ${({ $direction }) => ($direction === 'column' ? 'auto' : '44px')};
    }
  }

  /* Button styling within action bar */
  > button,
  > a {
    /* Ensure consistent button sizing */
    flex: ${({ $direction, $align }) => {
      if ($direction === 'row' && $align === 'space-between') {
        return '1';
      }
      return '0 0 auto';
    }};

    /* Prevent button text wrapping */
    white-space: nowrap;

    /* Consistent font weight */
    font-weight: ${({ theme }) => theme.fontWeights.normal};
  }

  /* Special handling for icon-only buttons */
  > button[aria-label]:not([aria-label='']) {
    min-width: 44px;
    padding: ${({ theme }) => theme.space.spacing10};
  }
`;
